import { useTranslation } from 'react-i18next';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  tableCellClasses,
} from '@mui/material';

import TextField from '../../../shared/components/TextField';
import { AreaUnit } from '../../../shared/graphql/graphql';
import { getCurrencySignForAreaUnit } from '../../../shared/helpers/functions/currency';
import {
  formatNumber,
  isNumber,
} from '../../../shared/helpers/functions/number';
import AmplitudeAnalytics from '../../../shared/services/AmplitudeAnalytics';
import {
  MAX_INPUT_VALUE,
  MIN_INPUT_VALUE,
} from '../../../shared/helpers/constants/ui';

export default function CalculatorTable({
  rows,
  total,
  areaUnit,
}: {
  rows: {
    title: string;
    quantity: number;
    quantityUnit: string;
    price?: number | null;
    amount?: number;
    onQuantityChange?: (value: string) => void;
    onQuantityInputBlur?: () => void;
  }[];
  total: {
    title: string;
    amount: number | null;
  };
  areaUnit: AreaUnit;
}) {
  const { t } = useTranslation();
  const currencySign = getCurrencySignForAreaUnit(areaUnit);
  const calculationValid = isNumber(total.amount);

  const validateQuantityInput = (value: string) => {
    const parsedValue = parseInt(value, 10);

    return (
      !isNumber(parsedValue) ||
      (MIN_INPUT_VALUE <= parsedValue && parsedValue <= MAX_INPUT_VALUE)
    );
  };

  const handleContactUsClick = () => {
    AmplitudeAnalytics.trackContactUs();
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow
            sx={{
              [`.${tableCellClasses.root}`]: {
                p: 1,
                fontSize: '12px',
                lineHeight: '16px',
                '&:first-of-type': { pl: 0 },
                '&:last-of-type': { pr: 0 },
              },
            }}
          >
            <TableCell>
              {t('plan-select.pricing-example.columns.quantity')}
            </TableCell>
            <TableCell sx={{ textAlign: 'right' }}>
              {t('plan-select.pricing-example.columns.price')}
            </TableCell>
            <TableCell sx={{ textAlign: 'right' }}>
              {t('plan-select.pricing-example.columns.amount')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const editable =
              !!row.onQuantityChange || !!row.onQuantityInputBlur;

            return (
              <TableRow
                key={index}
                sx={(theme) => ({
                  [`.${tableCellClasses.root}`]: {
                    p: 1,
                    fontSize: '12px',
                    '&:first-of-type': { pl: 0 },
                    '&:last-of-type': { pr: 0 },
                    color: theme.palette.secondary.main,
                  },
                  [`&:not(:last-child) .${tableCellClasses.root}`]: {
                    border: '0',
                  },
                })}
              >
                <TableCell sx={{ lineHeight: '16px' }}>
                  <Typography variant="body2" sx={{ fontSize: '12px' }}>
                    {row.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {editable ? (
                      <TextField
                        styles={{ formControl: { width: '80px' } }}
                        value={isNumber(row.quantity) ? row.quantity : ''}
                        inputProps={{
                          type: 'number',
                          min: MIN_INPUT_VALUE,
                          max: MAX_INPUT_VALUE,
                        }}
                        onChange={(e) => {
                          if (validateQuantityInput(e.target.value)) {
                            row.onQuantityChange?.(e.target.value);
                          }
                        }}
                        onBlur={() => row.onQuantityInputBlur?.()}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '12px', lineHeight: '16px' }}>
                        {formatNumber(row.quantity.toFixed(3))}
                      </Typography>
                    )}
                    <Typography
                      sx={{ fontSize: '12px', lineHeight: '16px', ml: 0.5 }}
                    >
                      {row.quantityUnit}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    lineHeight: editable ? '24px' : '16px',
                    verticalAlign: 'bottom',
                    textAlign: 'right',
                  }}
                >
                  {row.price != null && (
                    <>
                      {currencySign}
                      {formatNumber(row.price.toFixed(3))}
                    </>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    lineHeight: editable ? '24px' : '16px',
                    verticalAlign: 'bottom',
                    textAlign: 'right',
                  }}
                >
                  {isNumber(row.amount) && calculationValid && (
                    <>
                      {currencySign}
                      {formatNumber(row.amount.toFixed(1))}
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow
            sx={(theme) => ({
              [`.${tableCellClasses.root}`]: {
                p: 1,
                color: theme.palette.secondary.dark,
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '16px',
                border: '0',
                '&:first-of-type': { pl: 0 },
                '&:last-of-type': { pr: 0 },
              },
            })}
          >
            <TableCell>{total.title}</TableCell>
            <TableCell sx={{ textAlign: 'right' }} colSpan={2}>
              {calculationValid ? (
                <>
                  {total.amount ? currencySign : ''}
                  {total.amount ? formatNumber(total.amount.toFixed(1)) : ''}
                </>
              ) : (
                <Link
                  href={import.meta.env.VITE_DEMO_FORM_URL}
                  onClick={handleContactUsClick}
                >
                  {t('plan-select.price-volume-discounts.contact-us')}
                </Link>
              )}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
